import React, { useMemo } from 'react';
import { View, Text, Pressable, Animated, Platform } from 'react-native';
import { Lock, Shirt, Trophy } from 'lucide-react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import type { getFuturesCardStyles } from './FuturesCard.styles';
import type { Theme } from '../../../src/theme/getTheme';
import { FUTURES_CARD_COPY, type PickerType } from './FuturesCard.constants';

function buildFuturesPickAccessibilityLabel(input: {
  label: string;
  nameText: string | null | undefined;
  namePlaceholder: string;
  pickerDisabled: boolean;
  settled: boolean;
  settledCorrect: boolean | null | undefined;
}): string {
  const valueLine = (input.nameText?.trim() || input.namePlaceholder).trim();
  const header = `${input.label}. ${valueLine}`;
  if (input.settled) {
    if (input.settledCorrect === true) return `${header}. Correct.`;
    if (input.settledCorrect === false) return `${header}. Incorrect.`;
    return `${header}. Settled.`;
  }
  if (input.pickerDisabled) return `${header}. Locked.`;
  return header;
}

interface FuturesPickCardProps {
  pickKind: PickerType;
  label: string;
  media: React.ReactNode;
  nameText: string | null | undefined;
  namePlaceholder: string;
  pickerDisabled: boolean;
  isLocked: boolean;
  settled: boolean;
  settledCorrect: boolean | null | undefined;
  scale: Animated.Value;
  onPress: () => void;
  layoutTight: boolean;
  layoutNarrow: boolean;
  styles: ReturnType<typeof getFuturesCardStyles>;
  theme: Theme;
}

export const FuturesPickCard: React.FC<FuturesPickCardProps> = ({
  pickKind,
  label,
  media,
  nameText,
  namePlaceholder,
  pickerDisabled,
  isLocked,
  settled,
  settledCorrect,
  scale,
  onPress,
  layoutTight,
  layoutNarrow,
  styles,
  theme,
}) => {
  const headerIconSize = layoutTight ? 14 : layoutNarrow ? 15 : 16;
  const line1 = (nameText ?? namePlaceholder).toUpperCase();

  const accessibilityLabel = useMemo(
    () =>
      buildFuturesPickAccessibilityLabel({
        label,
        nameText,
        namePlaceholder,
        pickerDisabled,
        settled,
        settledCorrect,
      }),
    [label, namePlaceholder, nameText, pickerDisabled, settled, settledCorrect],
  );

  return (
    <Animated.View style={[styles.pickCardShell, { transform: [{ scale }] }]} accessible={false}>
      <Pressable
        onPress={pickerDisabled ? undefined : onPress}
        disabled={pickerDisabled}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        accessibilityState={{ disabled: pickerDisabled }}
        accessibilityHint={pickerDisabled ? undefined : FUTURES_CARD_COPY.A11Y_PICK_DOUBLE_TAP}
        style={({ pressed, hovered }) => [
          styles.pickCard,
          Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
          pressed && !pickerDisabled && styles.pickCardPressed,
          hovered && Platform.OS === 'web' && !pickerDisabled && styles.pickCardHover,
          pickerDisabled && styles.pickCardSettled,
        ]}
      >
        <View
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
        >
          <View style={styles.pickCardHeader}>
            {pickKind === 'winner' ? (
              <Trophy size={headerIconSize} color={theme.accentDark} fill={theme.accent} />
            ) : (
              <Shirt size={headerIconSize} color={theme.white} />
            )}
            <Text style={styles.pickCardHeaderLabel} numberOfLines={1}>
              {label}
            </Text>
          </View>
          <View style={styles.pickCardBody}>
            <View style={styles.pickCardMedia}>
              {media}
              {isLocked && (
                <View style={styles.lockBadge} pointerEvents="none">
                  <Lock size={layoutTight ? 10 : 11} color={theme.white} strokeWidth={2.5} />
                </View>
              )}
            </View>
          </View>
          <View style={styles.pickCardFooter}>
            <View style={styles.pickCardNameWrap}>
              <Text
                style={[styles.pickCardName, !nameText && styles.pickCardPlaceholder]}
                numberOfLines={2}
                ellipsizeMode="tail"
              >
                {line1}
              </Text>
            </View>
          </View>
          {!isLocked && settled ? (
            <View style={[styles.settledBadge, settledCorrect ? styles.settledCorrect : styles.settledWrong]}>
              <Text style={styles.settledBadgeText}>{settledCorrect ? '\u2705' : '\u274C'}</Text>
            </View>
          ) : null}
        </View>
      </Pressable>
    </Animated.View>
  );
};
